import React, { useState, useEffect, useRef, useCallback } from 'react';
import { client } from '../client';
import { motion } from 'framer-motion';
import imageUrlBuilder from '@sanity/image-url';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

const builder = imageUrlBuilder(client);

function urlFor(source) {
  return builder.image(source);
}

export default function Home() {
  const [photos, setPhotos] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const loadedCount = useRef(0);
  const touchStartX = useRef(null);
  const theme = useTheme();
  const darkMode = theme?.darkMode;

  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const goNext = useCallback(() => {
    setSelectedIndex((prev) => {
      const total = photos[0]?.images?.length || 0;
      return prev === total - 1 ? 0 : prev + 1;
    });
  }, [photos]);

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) => {
      const total = photos[0]?.images?.length || 0;
      return prev === 0 ? total - 1 : prev - 1;
    });
  }, [photos]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') setSelectedIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goNext, goPrev]);

  useEffect(() => {
    client
      .fetch(`*[_type == "gallery"] | order(orderNumber asc)`)
      .then((data) => setPhotos(data))
      .catch(console.error);
  }, []);

  if (!photos.length)
    return <h2 className="text-white text-center mt-20">Loading...</h2>;

  const images = photos[0]?.images || [];

  const handleImageLoad = () => {
    loadedCount.current += 1;
    if (loadedCount.current >= Math.min(images.length, 6)) {
      setImagesLoaded(true);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) {
      deltaX < 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  return (
    <>
      <main
        style={{
          color: darkMode ? '#ffffff' : '#0f172a',
          transition: 'all 0.3s ease',
        }}
        className="min-h-screen w-full flex flex-col overflow-x-hidden"
      >
        {/* Gallery */}
        <section className="w-full flex flex-col items-center flex-1">
          <div className="columns-2 sm:columns-2 lg:columns-3 gap-3 px-4 sm:p-9 mt-10 sm:mt-16 w-full">
            {images.map((img, index) => {
              const image = urlFor(img).url();
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="mb-3 sm:break-inside-avoid overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedIndex(index)}
                >
                  <img
                    src={image}
                    alt={`photo-${index}`}
                    className="w-full h-auto object-contain transition-transform duration-700"
                    loading="lazy"
                    onLoad={handleImageLoad}
                  />
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Modal */}
        {selectedIndex !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm"
              onClick={() => setSelectedIndex(null)}
            />

            {/* X button — mobile only */}
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 z-20 text-white text-3xl font-light sm:hidden"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Arrows — desktop only */}
            <button
              onClick={goPrev}
              className="absolute left-4 z-20 text-white text-7xl font-extralight px-10 py-6 hidden sm:block"
            >
              ‹
            </button>

            <motion.img
              key={selectedIndex}
              src={urlFor(images[selectedIndex]).url()}
              alt="full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative z-10 object-contain w-[90vw] sm:w-[70vw] lg:w-[60%] max-h-[90vh]"
            />

            <button
              onClick={goNext}
              className="absolute right-3 z-20 text-white/80 hover:text-white text-7xl font-extralight px-10 py-6 hidden sm:block"
            >
              ›
            </button>
          </div>
        )}

        {imagesLoaded && <Footer />}
      </main>
    </>
  );
}